import { jwtDecode as jwt_decode } from "jwt-decode";
import { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";

import { LoginResponseBodyType } from "@intelligems/ig-types";
import { api } from "@src/api/api";
import { useFastOrgSwitch } from "@src/api/organizations/switch-organization";
import { useUser } from "@src/api/user-auths/login";
import { GENERAL_ROUTES } from "@src/routes/routes";
import { JwtToken } from "@src/types/user";
import { IgStorage } from "@src/utils/igStorage";
import { useMutation } from "@tanstack/react-query";

const appLaunchedCallback = async () => {
  const urlParams = new URLSearchParams(window.location.search);

  const params = {
    skipRedirect: "true",
  };
  for (const value of urlParams.keys()) {
    params[value] = urlParams.get(value);
  }

  try {
    const resp = await api.get<LoginResponseBodyType>(`/v2/app_launched`, {
      params,
    });
    return resp.data;
  } catch (error) {
    return null;
  }
};

export const useAppLaunchedCallback = () => {
  return useMutation({
    mutationFn: async () => appLaunchedCallback(),
    onSuccess: (data) => {
      if (!data) {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else if (data.type === "redirect") {
        // This redirect should be like https://cajun-pizza.myshopify.com/admin/oauth/authorize?...
        // eslint-disable-next-line react-compiler/react-compiler
        window.location.href = data.redirect;
      } else if (data.type === "clear") {
        IgStorage.clearAuthTokens();
        window.location.reload();
      } else {
        window.location.href = "/login";
      }
    },
  });
};

export const useAppLaunch = () => {
  const [refetchInterval, setRefetchInterval] = useImmer<number | undefined>(
    undefined
  );
  const [pollCount, setPollCount] = useImmer(0);

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const token = IgStorage.authToken;
  const user = useUser({ refetchInterval });

  const switchOrg = useFastOrgSwitch();
  const appLaunchedCallback = useAppLaunchedCallback();

  const isInstalled =
    user.data?.type === "login" ? user.data.organization.installed : undefined;

  useEffect(() => {
    if (!token) {
      appLaunchedCallback.mutate();
      return;
    }

    const shopParam = searchParams.get("shop");
    if (shopParam) {
      const decodedToken = jwt_decode<JwtToken>(token);
      const isMatchingShop = decodedToken.shopId === shopParam;
      if (!isMatchingShop) {
        switchOrg.mutate(
          {
            token,
            shopId: shopParam,
          },
          {
            onSuccess: (data) => {
              appLaunchedCallback.mutate();
            },
            onError: (err) => {
              appLaunchedCallback.mutate();
            },
          }
        );
        return;
      }
    }

    if (
      user.isSuccess &&
      user.data.type === "login" &&
      !user.data.organization.installed
    ) {
      appLaunchedCallback.mutate();
      return;
    }
  }, [token, searchParams, user.isSuccess, user.data?.type, isInstalled]);

  useEffect(() => {
    if (!user.isSuccess || user.data.type !== "login") {
      return;
    }

    if (user.data.organization.installed) {
      setRefetchInterval(undefined);
      setPollCount(0);
      window.location.pathname = GENERAL_ROUTES.Index;
      return;
    }

    const MAX_POLL_ATTEMPTS = 60;

    if (pollCount >= MAX_POLL_ATTEMPTS) {
      setRefetchInterval(undefined);
      console.error(
        "Installation polling timed out after 2 minutes. Please try refreshing the page or contact support."
      );
      return;
    }

    setRefetchInterval(2000);
    setPollCount((count) => count + 1);

    return () => {
      setRefetchInterval(undefined);
    };
    // @ts-ignore
  }, [user.isSuccess, user.data?.type, user.data?.organization?.installed]);
};
